'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import { track, type PanoramaMethod } from '@/lib/telemetry';
import { cn } from '@/lib/utils';
import { DEFAULT_LAYOUT, WIDGETS, type WidgetId, type WidgetPlacement } from './widget-registry';
import { WidgetIdentityProvider } from './widget-identity';

// Canonical position of each widget for impression attribution — layout order,
// which is also the Panorama slide order for the hero widgets.
const WIDGET_POSITIONS = new Map(DEFAULT_LAYOUT.map((placement, index) => [placement.id, index]));

function WidgetById({ id }: { id: WidgetId }) {
  const { Component } = WIDGETS[id];
  return (
    <WidgetIdentityProvider id={id} position={WIDGET_POSITIONS.get(id) ?? -1}>
      <Component />
    </WidgetIdentityProvider>
  );
}

/**
 * Lays out the home widgets.
 *
 * Desktop mirrors the design: a hero row (Disponível + Ritmo), a trend row (three
 * equal cards), then the list widgets. The lists live in TWO INDEPENDENT flex
 * columns — a masonry — so a short card (e.g. an empty review queue) packs tight
 * instead of leaving a gap below it while a taller neighbour finishes its row. A
 * plain CSS grid row-locks each pair to its tallest cell, which is the gap bug.
 *
 * Mobile: the hero widgets ride a swipeable "Panorama" carousel; the rest stack.
 */
export function WidgetGrid({ layout = DEFAULT_LAYOUT }: { layout?: WidgetPlacement[] }) {
  const heroPlacements = layout.filter((p) => WIDGETS[p.id].hero);
  const restPlacements = layout.filter((p) => !WIDGETS[p.id].hero);

  return (
    <>
      {/* Mobile — Panorama swipe for the hero row, then the rest stacked. */}
      <div className="md:hidden">
        <PanoramaCarousel placements={heroPlacements} />
        <div className="mt-4 flex flex-col gap-4">
          {restPlacements.map((p) => (
            <WidgetById key={p.id} id={p.id} />
          ))}
        </div>
      </div>

      {/* Desktop — the design's structure. */}
      <div className="hidden flex-col gap-4 md:flex">
        {/* Hero: Disponível (2/3) + Ritmo (1/3), stretched to the same height so
            the row reads as one block (the prototype's front-liners). */}
        <div className="grid grid-cols-3 items-stretch gap-4">
          <div className="col-span-2 min-w-0">
            <WidgetById id="disponivel" />
          </div>
          <div className="min-w-0">
            <WidgetById id="ritmo" />
          </div>
        </div>

        {/* Trend: three equal cards, equal height. */}
        <div className="grid grid-cols-3 items-stretch gap-4">
          <div className="min-w-0">
            <WidgetById id="em-aberto" />
          </div>
          <div className="min-w-0">
            <WidgetById id="patrimonio" />
          </div>
          <div className="min-w-0">
            <WidgetById id="metas" />
          </div>
        </div>

        {/* Lists: two independent masonry columns — each packs to its own content. */}
        <div className="grid grid-cols-2 items-start gap-4">
          <div className="flex min-w-0 flex-col gap-4">
            <WidgetById id="revisar" />
            <WidgetById id="assinaturas" />
          </div>
          <div className="flex min-w-0 flex-col gap-4">
            {/* Transactions outrank the category breakdown (user call). */}
            <WidgetById id="ultimas" />
            <WidgetById id="categorias" />
          </div>
        </div>
      </div>
    </>
  );
}

function PanoramaCarousel({ placements }: { placements: WidgetPlacement[] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', containScroll: 'trimSnaps' });
  const [selected, setSelected] = useState(0);
  // How the NEXT selection change was initiated. Dots stamp 'dot' right before
  // scrolling; anything else that fires embla's 'select' is a swipe. 'initial'
  // covers the one synthetic sync after init, so "carousel seen at slide 0" and
  // real engagement stay distinguishable in analytics.
  const selectionMethodRef = useRef<PanoramaMethod>('initial');

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setSelected(index);
    const placement = placements[index];
    if (placement) {
      track('new_home.panorama_slide_viewed', {
        index,
        widget: placement.id,
        method: selectionMethodRef.current,
      });
    }
    selectionMethodRef.current = 'swipe';
  }, [emblaApi, placements]);

  const scrollToSlide = useCallback(
    (index: number) => {
      // Same-slide clicks fire no 'select', which would leave a stale 'dot'
      // stamp mislabeling the next swipe — skip them.
      if (!emblaApi || emblaApi.selectedScrollSnap() === index) return;
      selectionMethodRef.current = 'dot';
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  // A real control instead of the "deslize" hint (design review) — advances
  // card by card and wraps back to the first at the end. Swiping still works.
  // Stamps 'button' so analytics separates it from swipes; the single-slide
  // no-op is skipped for the same stale-stamp reason as the dots.
  const onAdvance = useCallback(() => {
    if (!emblaApi) return;
    const target = emblaApi.canScrollNext() ? emblaApi.selectedScrollSnap() + 1 : 0;
    if (emblaApi.selectedScrollSnap() === target) return;
    selectionMethodRef.current = 'button';
    emblaApi.scrollTo(target);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between px-1">
        <span className="font-mono text-2xs font-semibold tracking-[0.14em] text-text-tertiary uppercase">
          Panorama
        </span>
        <button
          type="button"
          onClick={onAdvance}
          aria-label="Próximo card do panorama"
          className="-m-1 cursor-pointer p-1 text-2xs text-text-tertiary transition-colors hover:text-text-secondary"
        >
          próximo ›
        </button>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-3">
          {placements.map((p) => (
            <div key={p.id} className="min-w-0 shrink-0 grow-0 basis-[86%]">
              <WidgetById id={p.id} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex justify-center gap-1.5">
        {placements.map((p, i) => (
          <PanoramaDot key={p.id} index={i} active={i === selected} onNavigate={scrollToSlide} />
        ))}
      </div>
    </div>
  );
}

function PanoramaDot({
  index,
  active,
  onNavigate,
}: {
  index: number;
  active: boolean;
  onNavigate: (index: number) => void;
}) {
  const handleClick = useCallback(() => {
    onNavigate(index);
  }, [onNavigate, index]);

  return (
    <button
      type="button"
      aria-label={`Ir para o card ${index + 1}`}
      onClick={handleClick}
      className={cn(
        'h-1.5 cursor-pointer rounded-full transition-all',
        active ? 'w-4 bg-brand-600' : 'w-1.5 bg-border-subtle',
      )}
    />
  );
}
